import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Paper,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Key as KeyIcon,
  SecurityOutlined,
  Visibility,
  VisibilityOff,
  Save
} from '@mui/icons-material';
import { Region } from '../../../types';
import { AssignableRole } from '../../../allUsersApi';
import { ZERDE_PRIMARY } from '../types';

interface UserSecuritySectionProps {
  canEditPassword: boolean;
  canEditRole: boolean;
  /** users.edit_pharmacy: номер аптеки и регион сервер проверяет одним правом */
  canEditPharmacyNum: boolean;
  /** Карточка самого оператора: свой пароль меняется только с вводом текущего */
  isSelf: boolean;
  currentPassword: string;
  setCurrentPassword: (v: string) => void;
  newPassword: string;
  setNewPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  savingPassword: boolean;
  handleSavePassword: () => void;
  role: string;
  setRole: (v: string) => void;
  cityId: string;
  setCityId: (v: string) => void;
  pharmacyNum: string;
  setPharmacyNum: (v: string) => void;
  regions: Region[];
  /** Роли из GET /api/v2/users/roles: assignable уже посчитан сервером по правилу «ниже моей» */
  roles: AssignableRole[];
  savingSettings: boolean;
  handleSaveSettings: () => void;
}

export const UserSecuritySection: React.FC<UserSecuritySectionProps> = ({
  canEditPassword,
  canEditRole,
  canEditPharmacyNum,
  isSelf,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  showPassword,
  setShowPassword,
  savingPassword,
  handleSavePassword,
  role,
  setRole,
  cityId,
  setCityId,
  pharmacyNum,
  setPharmacyNum,
  regions,
  roles,
  savingSettings,
  handleSaveSettings
}) => {
  const allowedRoles = React.useMemo(() => roles.filter(r => r.assignable), [roles]);
  const currentRoleName = roles.find(r => r.slug === role)?.name || role;
  const showSettings = canEditRole || canEditPharmacyNum;

  return (
    <Box id="user-section-security" sx={{ scrollMarginTop: '60px', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {(canEditPassword || isSelf) && (
        <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <KeyIcon sx={{ color: ZERDE_PRIMARY, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Смена пароля аккаунта
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            {isSelf
              ? 'Для смены своего пароля введите текущий пароль. Другие ваши сессии будут завершены'
              : 'Установите новый надежный пароль для входа сотрудника в систему'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
            {isSelf && (
              <TextField
                label="Текущий пароль"
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                size="small"
                fullWidth
                autoComplete="current-password"
                InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
              />
            )}
            <TextField
              label="Новый пароль"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              size="small"
              fullWidth
              autoComplete="new-password"
              InputProps={{
                sx: { bgcolor: '#f8fafc', borderRadius: '8px' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              disabled={!newPassword.trim() || (isSelf && !currentPassword) || savingPassword}
              onClick={handleSavePassword}
              sx={{
                minWidth: 170,
                height: 40,
                bgcolor: ZERDE_PRIMARY,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#105ec2' }
              }}
            >
              {savingPassword ? <CircularProgress size={16} /> : 'Обновить пароль'}
            </Button>
          </Box>
        </Paper>
      )}

      {showSettings && (
        <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <SecurityOutlined sx={{ color: ZERDE_PRIMARY, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Системные права и привязка к аптеке
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
            Управление системной ролью, регионом и номером филиала
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
            {canEditRole && (
              <FormControl size="small" fullWidth disabled={allowedRoles.length === 0}>
                <InputLabel>Системная роль</InputLabel>
                <Select
                  value={role}
                  label="Системная роль"
                  onChange={(e) => setRole(e.target.value)}
                  sx={{ bgcolor: '#f8fafc', borderRadius: '8px' }}
                >
                  {/* Текущая роль вне списка назначаемых показывается отдельно и не выбирается */}
                  {role && !allowedRoles.some(r => r.slug === role) && (
                    <MenuItem value={role} disabled>
                      {currentRoleName} (Текущая роль)
                    </MenuItem>
                  )}
                  {allowedRoles.map(r => (
                    <MenuItem key={r.slug} value={r.slug}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
                {allowedRoles.length === 0 && (
                  <Typography variant="caption" sx={{ color: '#d97706', mt: 0.5, display: 'block' }}>
                    Нет доступных ролей ниже вашего ранга для назначения
                  </Typography>
                )}
              </FormControl>
            )}

            {canEditPharmacyNum && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Регион / Город</InputLabel>
                  <Select
                    value={cityId}
                    label="Регион / Город"
                    onChange={(e) => setCityId(e.target.value)}
                    sx={{ bgcolor: '#f8fafc', borderRadius: '8px' }}
                  >
                    <MenuItem value=""><em>Не привязан</em></MenuItem>
                    {regions.map(reg => (
                      <MenuItem key={reg.id} value={String(reg.id)}>{reg.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Номер аптеки / филиала"
                  value={pharmacyNum}
                  onChange={(e) => setPharmacyNum(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
                />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={savingSettings}
              onClick={handleSaveSettings}
              startIcon={savingSettings ? <CircularProgress size={16} /> : <Save />}
              sx={{
                bgcolor: ZERDE_PRIMARY,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 3,
                '&:hover': { bgcolor: '#105ec2' }
              }}
            >
              Сохранить параметры
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default UserSecuritySection;
